import { createApp } from 'vue';
import axios from 'axios';
import { createGtm } from '@gtm-support/vue-gtm';
import './plugins/VeeValidate';
import * as Sentry from '@sentry/vue';
import { ClickfireTrackerVuePlugin as ClickfireTracker } from '@sunlabde/m2m-clickfire-tracker-plugin';
import m2mLibrary, { VueTippy } from '@sunlabde/m2m-library';
import '@sunlabde/m2m-library/dist/style.css';
import { VueQueryPlugin } from '@tanstack/vue-query';
import focusDirective from './directives/focus';
import { pinia } from './plugins/pinia';
import App from './App.vue';
import i18n from './plugins/i18n';
import router from './router';
import { useAuthStore } from './store/auth';
import { CLICKFIRE_URL } from './utilities/Constants';
import { isForeignRangeError } from './utilities/SentryHelper';
import { addSignupPortal, createSignupPortal } from './models/SignupPortal';
/*
We need to import the styles via js, otherwise we get the following error random in a loop and are unable to start the dev environment:
[vite] Internal server error: [postcss] Maximum call stack size exceeded

We also get this error, every time the starts locally on dev mode
hit save in index.css and voila the error gets triggered.
Dev Server needs a restart otherwise we won't get out of this error loop locally.
I'm pretty sure this goes hand in hand with the m2m-library.
Maybe the tailwind config is broken at some point or it's related to the mulitple
tailwind css files wich are importet from Webapp and Library.
As soon as the m2m-library is reintegratet we should immediatly debug this issue.

It seems also to be in relation with the following issue on GitHub:

https://github.com/tailwindlabs/tailwindcss/issues/8582
*/
import './styles/animations/fade.css';
import './styles/animations/step.css';
import './index.css';
import intercom from './plugins/intercom';
import { appProgressLoader } from './plugins/AppProgressLoader';
import { registerGlobalComponents } from './plugins/GlobalComponents';
import { plausible } from './plugins/plausible/setup';
import LimangoLogo from '@/assets/logo_limango.svg';

const app = createApp(App);

axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.headers.common.Accept = 'application/json';

if (import.meta.env.VITE_ENVIRONMENT !== 'local-dev') {
	// Disable Sentry integration for local dev environment

	Sentry.init({
		app,
		dsn: 'https://e92b47092798ac07c049e933122b44d2@o4509711156707333.ingest.de.sentry.io/4509807437938768',
		integrations: [
			Sentry.browserTracingIntegration({ router }),
			Sentry.eventFiltersIntegration({
				denyUrls: ['local.signup.music2me.com'],
				ignoreErrors: [
					'Non-Error exception captured',
					'Non-Error promise rejection captured',
					'fbq is not defined',
					'Failed to fetch dynamically imported module',
					'Unable to preload CSS',
				],
			}),
		],
		tracesSampleRate: 1.0,
		tracePropagationTargets: [/^https:\/\/api\.music2me\.com/, /^\//],
		environment: import.meta.env.VITE_ENVIRONMENT,
		release: __COMMIT_REF__,
		enableLogs: true,
		beforeSend: (event) => (isForeignRangeError(event) ? null : event),
		// Requests carry passwords (oauth/token, POST user) and responses carry tokens, so never send cookies, auth headers or HTTP bodies.
		dataCollection: {
			userInfo: true,
			cookies: false,
			httpHeaders: {
				request: { deny: ['authorization', 'cookie'] },
				response: { deny: ['authorization', 'cookie'] },
			},
			httpBodies: [],
			queryParams: true,
		},
	});
}

app.use(
	createGtm({
		id: 'GTM-K5GGW2Z',
		enabled: true,
		debug: false,
		vueRouter: router,
	}),
);

app.use(intercom, {
	app_id: import.meta.env.VITE_INTERCOM_APP_ID,
});

app.use(plausible);
app.use(VueTippy, {
	directive: 'tippy',
	defaultProps: {
		theme: 'info',
		animation: 'shift-toward',
		arrow: true,
	},
});
app.use(pinia);
app.use(ClickfireTracker, { router, url: CLICKFIRE_URL });
app.use(router);
app.use(appProgressLoader, { router });
app.use(i18n);
app.use(VueQueryPlugin);
app.use(m2mLibrary, { i18n });

app.directive('focus', focusDirective);

registerGlobalComponents(app);

const {
	setupAuthenticationHooks,
} = useAuthStore();

setupAuthenticationHooks();

addSignupPortal([
	createSignupPortal('limango', { logo: LimangoLogo, trial: false, hasPlans: true }),
	createSignupPortal('neujahr21', { trial: false, hasPlans: false }),
	createSignupPortal('cyberweek', {
		trial: false,
		discountCode: import.meta.env.VITE_DISCOUNT_CYBERWEEK_70,
	}),
	createSignupPortal('launchMonth', { trial: true, hasPlans: true }),
	createSignupPortal('cyberweek24', { trial: true, hasPlans: true }),
	createSignupPortal('cyberspecial24', { trial: true, hasPlans: true }),
	createSignupPortal('holiday24', { trial: true, hasPlans: true }),
	createSignupPortal('plussies', { trial: true, hasPlans: true }),
	createSignupPortal('plus24', { trial: true, hasPlans: true }),
	createSignupPortal('plus', { trial: true, hasPlans: true }),
	createSignupPortal('vday25', { trial: true, hasPlans: true }),
	createSignupPortal('ostern25', { trial: true, hasPlans: true }),
	createSignupPortal('summer25', { trial: true, hasPlans: true }),
	createSignupPortal('b2s25', { trial: true, hasPlans: true }),
	createSignupPortal('halloween25', { trial: true, hasPlans: true }),
	createSignupPortal('warmup-wednesday', { trial: true, hasPlans: true }),
	createSignupPortal('black-week', { trial: true, hasPlans: true }),
	createSignupPortal('black-friday', { trial: true, hasPlans: true }),
	createSignupPortal('prexmas25', { trial: true, hasPlans: true }),
	createSignupPortal('xmas25', { trial: true, hasPlans: true }),
	createSignupPortal('newyear26', { trial: true, hasPlans: true }),
	createSignupPortal('newyearII', { trial: true, hasPlans: true }),
	createSignupPortal('birthday', { trial: true, hasPlans: true }),
	createSignupPortal('ostern26', { trial: true, hasPlans: true }),
	createSignupPortal('mayday26', { trial: true, hasPlans: true }),
	createSignupPortal('makemusicday2026', { trial: true, hasPlans: true }),
	createSignupPortal('welcomeback', { trial: true, hasPlans: true }),
	createSignupPortal('september-sale', { trial: true, hasPlans: true }),
	createSignupPortal('september-winback', { trial: true, hasPlans: true }),
	createSignupPortal('newsletter', { trial: true, hasPlans: true }),
	createSignupPortal('halloween2026', { trial: true, hasPlans: true }),
]);

router.isReady().then(async () => {
	app.mount('#app');
});
